import type { DetailsSectionProps } from '@hotjar/feedback/components/common/Wizard/Sections/Details/DetailsSection';
import { DetailsSection } from '@hotjar/feedback/components/common/Wizard/Sections/Details/DetailsSection';
import type { IntegrationsSectionProps } from '@hotjar/feedback/components/common/Wizard/Sections/Integrations/IntegrationsSection';
import { IntegrationsSection } from '@hotjar/feedback/components/common/Wizard/Sections/Integrations/IntegrationsSection';
import type {
  SurveySectionKey,
  SurveySectionProps,
  WizardStep,
  WizardSummary,
} from '@hotjar/feedback/domain/common/Wizard/Wizard';
import type { WidgetPreviewProps } from '@hotjar/feedback/components/common/Wizard/WidgetPreview/WidgetPreview';
import { WidgetPreview } from '@hotjar/feedback/components/common/Wizard/WidgetPreview';
import type { BehaviorSectionProps } from '../Behavior/BehaviorSection/BehaviorSection';
import { BehaviorSection } from '../Behavior/BehaviorSection/BehaviorSection';
import type { SurveyInvitationProps } from '../Invitation/Invitation';
import { SurveyInvitation } from '../Invitation/Invitation';
import type { SurveyLocationProps } from '../Location/SurveyLocation';
import { SurveyLocation } from '../Location/SurveyLocation';
import type { QuestionsSectionProps } from '../Questions/QuestionsSection/QuestionsSection';
import { QuestionsSection } from '../Questions/QuestionsSection/QuestionsSection';
import type { AppearanceSectionProps } from '../Sections/Appearance/AppearanceSection';
import { AppearanceSection } from '../Sections/Appearance/AppearanceSection';
import { useSummarySection } from '../Summary/useSummarySection';
import type { TargetingSectionProps } from '../Targeting/TargetingSection';
import { TargetingSection } from '../Targeting/TargetingSection';
import { useSectionsTitles } from './useSectionsTitles';
import type { ContentlessWizardStep } from './useWizardSteps';

export const useDetailsSectionContent = (
  activeSection: SurveySectionKey,
  detailsProps: DetailsSectionProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent: activeSection === 'name' ? <DetailsSection {...detailsProps} /> : null,
    sideContent: null,
  };
};

export const useLocationSectionContent = (
  activeSection: SurveySectionKey,
  locationProps: SurveyLocationProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent: activeSection === 'location' ? <SurveyLocation {...locationProps} /> : null,
    sideContent: null,
  };
};

export const useQuestionsSectionContent = (
  activeSection: SurveySectionKey,
  questionsProps: QuestionsSectionProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent: activeSection === 'questions' ? <QuestionsSection {...questionsProps} /> : null,
    sideContent: null,
  };
};

export const useAppearanceSectionContent = (
  activeSection: SurveySectionKey,
  appearanceProps: AppearanceSectionProps,
  previewProps: WidgetPreviewProps,
): { mainContent: JSX.Element | null; sideContent: JSX.Element | null } => {
  return activeSection === 'appearance'
    ? {
        mainContent: <AppearanceSection {...appearanceProps} />,
        sideContent: <WidgetPreview {...previewProps} />,
      }
    : { mainContent: null, sideContent: null };
};

export const useInvitationSectionContent = (
  activeSection: SurveySectionKey,
  invitationProps: SurveyInvitationProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent: activeSection === 'invitation' ? <SurveyInvitation {...invitationProps} /> : null,
    sideContent: null,
  };
};

export const useBehaviorSectionContent = (
  activeSection: SurveySectionKey,
  behaviorProps: BehaviorSectionProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent: activeSection === 'behavior' ? <BehaviorSection {...behaviorProps} /> : null,
    sideContent: null,
  };
};

export const useIntegrationsSectionContent = (
  activeSection: SurveySectionKey,
  integrationsProps: IntegrationsSectionProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent:
      activeSection === 'forwarding' ? <IntegrationsSection {...integrationsProps} /> : null,
    sideContent: null,
  };
};

export const useTargetingSectionContent = (
  activeSection: SurveySectionKey,
  targetingProps: TargetingSectionProps,
): { mainContent: JSX.Element | null; sideContent: null } => {
  return {
    mainContent: activeSection === 'targeting' ? <TargetingSection {...targetingProps} /> : null,
    sideContent: null,
  };
};

export const useSectionsContent = (
  wizardSteps: ContentlessWizardStep[],
  activeSection: SurveySectionKey,
  {
    name,
    description,
    showErrors,
    onNameChanged,
    onDescriptionChanged,
    location,
    onChangeLocation,
    connectVisitData,
    onConnectVisitDataChange,
    reloadPreview,
    previewConfig,
    questions,
    thankYouMessage,
    language,
    onThankYouMessageChange,
    onAddQuestionFromQuestionBank,
    onAddNewQuestion,
    onRemoveQuestion,
    onUpdateQuestion,
    onShowLegalChange,
    onChangeOrder,
    onPreviewReaction,
    isValidateEnabled,
    isThankYouValid,
    isPopoverSurvey,
    isDataFromTemplate,
    hasLegalDetails,
    hasSurveyResponses,
    showLegal,
    areQuestionsValid,
    onLanguageChanged,
    backgroundColor,
    onChangeBackgroundColor,
    onPreviewBackgroundColor,
    buttonColor,
    onChangeButtonColor,
    onPreviewButtonColor,
    fontColor,
    onChangeFontColor,
    onPreviewFontColor,
    showSurveyPosition,
    position,
    positionOptions,
    onPositionChange,
    logoSource,
    logoError,
    onChangeLogo,
    onRemoveLogo,
    hideBranding,
    onChangeBranding,
    hidePreviewActions,
    showBrowserFrame,
    widget,
    widgetConfig,
    iframeId,
    invitation,
    isInvitationEnabled,
    onInvitationChange,
    setInviteEnabled,
    isOffsite,
    displayCondition,
    displayDelay,
    onDisplayConditionChanged,
    onDisplayDelayChanged,
    persistCondition,
    onPersistConditionChanged,
    isAbandonDisabled,
    allQuestions,
    slackSelectedQuestions,
    msTeamsSelectedQuestions,
    onSlackQuestionsChange,
    onMsTeamsQuestionsChange,
    slack,
    email,
    msTeams,
    zapier,
    onChange,
    emailLimitsHelpUrl,
    siteId,
    pollId,
    hasTargetingErrors,
    targeting,
    userAttributes,
    onTargetingChange,
    onUpsell,
    targetingAutoValidate,
    activeForwardingNames,
    mode,
    targetingPercentage,
    onTargetingPercentageChange,
    areAllVisibleSectionsValid,
    errors,
    forwardingEmailAddresses,
    forwardingEmailEnabled,
    forwardingMsTeamsEnabled,
    forwardingMsTeamsWebhookUrls,
    forwardingSlackChannels,
    forwardingSlackEnabled,
    isActive,
    isSaving,
    onStatusChange,
    onCopy,
    onNavigateClicked,
    onSave,
    webhooks,
    forwardingWebhooksEnabled,
    forwardingWebhooksUrls,
  }: SurveySectionProps,
): { sections: WizardStep<SurveySectionKey>[]; summarySection: WizardSummary } => {
  const detailsProps = { name, description, showErrors, onNameChanged, onDescriptionChanged };
  const locationProps = { location, onChangeLocation };
  const questionsProps = {
    connectVisitData,
    onConnectVisitDataChange,
    reloadPreview,
    previewConfig,
    questions,
    thankYouMessage,
    language,
    onThankYouMessageChange,
    onAddQuestionFromQuestionBank,
    onAddNewQuestion,
    onRemoveQuestion,
    onUpdateQuestion,
    onShowLegalChange,
    onChangeOrder,
    onPreviewReaction,
    isValidateEnabled,
    isThankYouValid,
    isPopoverSurvey,
    isDataFromTemplate,
    hasLegalDetails,
    hasSurveyResponses,
    showLegal,
    areQuestionsValid,
  };
  const appearanceProps = {
    language,
    onLanguageChanged,
    backgroundColor,
    onChangeBackgroundColor,
    onPreviewBackgroundColor,
    buttonColor,
    onChangeButtonColor,
    onPreviewButtonColor,
    fontColor,
    onChangeFontColor,
    onPreviewFontColor,
    showFontColor: isOffsite === false,
    showSurveyPosition,
    position,
    positionOptions,
    onPositionChange,
    logoSource,
    logoError,
    onChangeLogo,
    onRemoveLogo,
    hideBranding,
    onChangeBranding,
  };

  const invitationProps = {
    invitation,
    isInvitationEnabled,
    onInvitationChange,
    setInviteEnabled,
    showBranding: hideBranding === false,
  };

  const behaviorProps = {
    isOffsite,
    displayCondition,
    displayDelay,
    onDisplayConditionChanged,
    onDisplayDelayChanged,
    persistCondition,
    onPersistConditionChanged,
    isAbandonDisabled,
    showDisplaySettings: isOffsite === false || isInvitationEnabled,
  };

  const integrationsProps = {
    allQuestions,
    slackSelectedQuestions,
    msTeamsSelectedQuestions,
    onSlackQuestionsChange,
    onMsTeamsQuestionsChange,
    slack,
    email,
    msTeams,
    zapier,
    onChange,
    emailLimitsHelpUrl,
    siteId,
    pollId,
    webhooks,
  };

  const previewProps = {
    hidePreviewActions,
    showBrowserFrame,
    showDeviceSelector: isOffsite === false,
    widget,
    widgetConfig,
    iframeId,
  };

  const targetingProps = {
    targeting,
    hasTargetingErrors,
    isOffsite,
    targetingAutoValidate,
    userAttributes,
    targetingPercentage,
    onTargetingPercentageChange,
    onTargetingChange,
    onUpsell,
  };

  const summaryProps = {
    name,
    description,
    questions,
    location,
    targeting,
    targetingPercentage,
    mode,
    areAllVisibleSectionsValid,
    errors,
    forwardingEmailAddresses,
    forwardingEmailEnabled,
    forwardingMsTeamsEnabled,
    forwardingMsTeamsWebhookUrls,
    forwardingSlackChannels,
    forwardingSlackEnabled,
    isActive,
    isSaving,
    onStatusChange,
    onCopy,
    onNavigateClicked,
    onSave,
    reloadPreview,
    forwardingWebhooksEnabled,
    forwardingWebhooksUrls,
  };

  const detailsContent = useDetailsSectionContent(activeSection, detailsProps);
  const locationContent = useLocationSectionContent(activeSection, locationProps);
  const questionsContent = useQuestionsSectionContent(activeSection, questionsProps);
  const appearanceContent = useAppearanceSectionContent(
    activeSection,
    appearanceProps,
    previewProps,
  );
  const invitationContent = useInvitationSectionContent(activeSection, invitationProps);
  const behaviorContent = useBehaviorSectionContent(activeSection, behaviorProps);
  const integrationsContent = useIntegrationsSectionContent(activeSection, integrationsProps);
  const targetingContent = useTargetingSectionContent(activeSection, targetingProps);

  const contentMap = {
    name: detailsContent,
    location: locationContent,
    questions: questionsContent,
    appearance: appearanceContent,
    invitation: invitationContent,
    targeting: targetingContent,
    behavior: behaviorContent,
    forwarding: integrationsContent,
    review: { mainContent: null, sideContent: null },
  };

  const sectionsTitles = useSectionsTitles({
    ...detailsProps,
    ...locationProps,
    ...questionsProps,
    ...appearanceProps,
    ...previewProps,
    ...invitationProps,
    ...behaviorProps,
    ...integrationsProps,
    ...targetingProps,
    ...summaryProps,
    activeForwardingNames,
  } as SurveySectionProps);

  const summarySection = useSummarySection(summaryProps, previewProps);

  return {
    sections: wizardSteps.map((wizardStep) => {
      return {
        ...wizardStep,
        mainContent: contentMap[wizardStep.key].mainContent,
        sideContent: contentMap[wizardStep.key].sideContent,
        title: sectionsTitles[wizardStep.key]?.title ?? '',
        summary: sectionsTitles[wizardStep.key]?.summary ?? '',
      };
    }),
    summarySection,
  };
};
